<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
    registeredDomainId: { type: Number, required: true },
});

const status = ref(null);
const pollHandle = ref(null);

const requestedPresets = computed(() => status.value?.requested_presets ?? []);
const registrationFailed = computed(
    () => status.value?.registration_failed === true
);
const registrationRequiresReview = computed(
    () => status.value?.registration_requires_review === true
);

const fetchStatus = async () => {
    try {
        const { data } = await axios.get(
            route('settings.domains.buy.status', props.registeredDomainId)
        );
        status.value = data;
        const siteDone =
            !data.requested_presets?.includes('myflow_site') ||
            data.site_validated;
        const emailDone =
            !data.requested_presets?.includes('myflow_email') ||
            data.email_validated;

        if (
            data.registration_failed ||
            data.registration_requires_review ||
            (data.status === 'active' && siteDone && emailDone)
        ) {
            stopPolling();
        }
    } catch (e) {
        // ignore — try again
    }
};

const stopPolling = () => {
    if (pollHandle.value) {
        clearInterval(pollHandle.value);
        pollHandle.value = null;
    }
};

const items = computed(() => {
    const result = [
        {
            key: 'registered',
            label: registrationFailed.value
                ? 'Domain registration failed'
                : registrationRequiresReview.value
                ? 'Registration needs review'
                : 'Domain registered',
            done: status.value?.status === 'active',
            failed:
                registrationFailed.value || registrationRequiresReview.value,
        },
    ];

    if (requestedPresets.value.includes('myflow_site')) {
        result.push({
            key: 'site',
            label: 'Website connected',
            done: !!status.value?.site_validated,
            failed: false,
        });
    }
    if (requestedPresets.value.includes('myflow_email')) {
        result.push({
            key: 'email',
            label: 'Email connected',
            done: !!status.value?.email_validated,
            failed: false,
        });
    }

    return result;
});

const allDone = computed(() => items.value.every((i) => i.done));
const heading = computed(() => {
    if (registrationFailed.value) {
        return 'Could not register the domain';
    }
    if (registrationRequiresReview.value) {
        return 'Registration needs review';
    }
    if (!allDone.value) {
        return 'Setting up your domain…';
    }

    return status.value?.domain
        ? `You're live at ${status.value.domain}!`
        : "You're live!";
});

onMounted(() => {
    fetchStatus();
    pollHandle.value = setInterval(fetchStatus, 5000);
});

onBeforeUnmount(stopPolling);
</script>

<template>
    <div class="mx-auto w-full max-w-xl space-y-6 p-6 text-center">
        <h1 class="text-2xl font-semibold">{{ heading }}</h1>
        <p class="text-sm text-gray-600">
            {{
                status?.registration_message ??
                (allDone
                    ? 'Your domain is registered and connected, but it may take up to 24 hours before it is fully accessible by everyone.'
                    : "We're connecting your domain. This usually takes under a minute.")
            }}
        </p>

        <ul class="space-y-3 text-left">
            <li v-for="i in items" :key="i.key">
                <card-sub>
                    <div class="flex items-center gap-3">
                        <span
                            class="flex h-6 w-6 items-center justify-center rounded-full"
                            :class="
                                i.failed
                                    ? 'bg-red-100 text-red-700'
                                    : i.done
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-400 animate-pulse'
                            "
                        >
                            <i v-if="i.failed" class="fa-regular fa-xmark"></i>
                            <i
                                v-else-if="i.done"
                                class="fa-regular fa-check"
                            ></i>
                            <i v-else class="fa-regular fa-clock"></i>
                        </span>
                        <span
                            :class="
                                i.failed
                                    ? 'font-medium text-red-700'
                                    : i.done
                                    ? 'font-medium'
                                    : 'text-gray-500'
                            "
                        >
                            {{ i.label }}
                        </span>
                    </div>
                </card-sub>
            </li>
        </ul>

        <div class="pt-4">
            <a
                :href="route('settings.company.settings.domains')"
                class="text-sm mf-link"
                >Back to domain settings</a
            >
        </div>
    </div>
</template>
